import { cva } from 'class-variance-authority';
import { clsx } from 'clsx';
import { type ComponentPropsWithRef } from 'react';

import { type MarginUtilityClass } from '@repo/ui/utils/spacing';

import s from './Separator.module.css';

export const separatorOrientationOptions = ['horizontal', 'vertical'] as const;
export type SeparatorOrientationOption =
  (typeof separatorOrientationOptions)[number];

export const separatorSizeOptions = ['sm', 'md', 'lg', 'xl'] as const;
export type SeparatorSize = (typeof separatorSizeOptions)[number];

export type SeparatorProps = ComponentPropsWithRef<'div'> & {
  className?: string;
  isDecorative?: boolean;
  margin?: MarginUtilityClass | MarginUtilityClass[];
  orientation?: SeparatorOrientationOption;
  size?: SeparatorSize;
};

export const separatorVariants = cva(s.separator, {
  variants: {
    orientation: {
      horizontal: s.horizontalOrientation,
      vertical: s.verticalOrientation,
    },
    size: {
      sm: s.smSize,
      md: s.mdSize,
      lg: s.lgSize,
      xl: s.xlSize,
    },
  },
});

export const Separator = (props: SeparatorProps) => {
  const {
    isDecorative = false,
    margin,
    orientation = 'horizontal',
    size = 'sm',
    className,
    ...attrs
  } = props;

  const ariaOrientation = orientation === 'vertical' ? orientation : undefined;
  const semanticProps = isDecorative
    ? { role: 'none' }
    : { 'aria-orientation': ariaOrientation, role: 'separator' };

  return (
    <div
      className={clsx(
        separatorVariants({
          orientation,
          size,
          className,
        }),
        margin,
      )}
      data-orientation={orientation}
      {...semanticProps}
      {...attrs}
    />
  );
};
